import React, { useEffect, useMemo, useState } from "react";
import { Checkbox, Input, Modal, Spin, Table, notification } from "antd";
import { CloseOutlined, SearchOutlined } from "@ant-design/icons";
import { LuX } from "react-icons/lu";
import {
  useCreatePanelUserMutation,
  useGetPanelUserRespondentsQuery,
  useGetPanelUsersQuery,
} from "../../store/api/apiSlice";
import {
  buildImportPanelUserPayloads,
  getPanelUserEmailSet,
  mapRespondentsToImportRows,
} from "../../pages/admin/PanelUser/helpers";

const ImportPanelUsersModal = ({ visible, onClose, onSuccess }) => {
  const [searchInput, setSearchInput] = useState("");
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);

  const {
    data: respondentsData,
    isLoading: isRespondentsLoading,
    isFetching: isRespondentsFetching,
  } = useGetPanelUserRespondentsQuery(undefined, {
    skip: !visible,
    refetchOnMountOrArgChange: true,
  });

  const { data: panelUsersData } = useGetPanelUsersQuery(
    {},
    {
      skip: !visible,
    },
  );

  const [createPanelUser, { isLoading: isImporting }] =
    useCreatePanelUserMutation();

  useEffect(() => {
    if (!visible) {
      setSearchInput("");
      setSelectedRowKeys([]);
    }
  }, [visible]);

  const existingPanelUserEmails = useMemo(
    () => getPanelUserEmailSet(panelUsersData),
    [panelUsersData],
  );

  const respondentRows = useMemo(() => {
    const rows = mapRespondentsToImportRows(respondentsData).map((row) => ({
      ...row,
      isAlreadyPanelUser: existingPanelUserEmails.has(
        String(row.email).trim().toLowerCase(),
      ),
    }));

    const query = searchInput.trim().toLowerCase();

    if (!query) {
      return rows;
    }

    return rows.filter(
      (row) =>
        String(row.full_name || "")
          .toLowerCase()
          .includes(query) ||
        String(row.email || "")
          .toLowerCase()
          .includes(query),
    );
  }, [respondentsData, existingPanelUserEmails, searchInput]);

  const importableRows = useMemo(
    () => respondentRows.filter((row) => !row.isAlreadyPanelUser),
    [respondentRows],
  );

  const importableSelectedCount = useMemo(
    () =>
      importableRows.filter((row) => selectedRowKeys.includes(row._id)).length,
    [importableRows, selectedRowKeys],
  );

  const rowSelection = useMemo(
    () => ({
      selectedRowKeys,
      preserveSelectedRowKeys: true,
      columnTitle: (
        <Checkbox
          checked={
            importableRows.length > 0 &&
            importableRows.every((row) => selectedRowKeys.includes(row._id))
          }
          onChange={(e) => {
            setSelectedRowKeys(
              e.target.checked ? importableRows.map((row) => row._id) : [],
            );
          }}
        />
      ),
      onChange: (keys) => {
        setSelectedRowKeys(keys);
      },
      getCheckboxProps: (record) => ({
        disabled: record.isAlreadyPanelUser,
      }),
    }),
    [selectedRowKeys, importableRows],
  );

  const columns = [
    {
      title: "Full name",
      dataIndex: "full_name",
      key: "full_name",
      render: (full_name) => (
        <span className="font-medium text-gray-900">{full_name || "N/A"}</span>
      ),
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      render: (email, record) => (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-gray-900">{email}</span>
          {record.isAlreadyPanelUser ? (
            <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-500">
              Already added
            </span>
          ) : null}
        </div>
      ),
    },
  ];

  const handleImport = async () => {
    const selectedRows = importableRows.filter((row) =>
      selectedRowKeys.includes(row._id),
    );

    const payloads = buildImportPanelUserPayloads(selectedRows);

    if (!payloads.length) {
      notification.error({
        message: "No users selected",
        description:
          "Select at least one respondent who is not already a panel user.",
      });

      return;
    }

    try {
      const results = await Promise.allSettled(
        payloads.map((payload) => createPanelUser(payload).unwrap()),
      );

      const successCount = results.filter(
        (result) => result.status === "fulfilled",
      ).length;
      const failedCount = results.length - successCount;

      if (!successCount) {
        const firstError = results.find(
          (result) => result.status === "rejected",
        );

        notification.error({
          message: "Error",
          description:
            firstError?.reason?.data?.message ||
            "Failed to import selected panel users",
        });

        return;
      }

      notification.success({
        message: "Success",
        description:
          failedCount > 0
            ? `${successCount} panel user${
                successCount === 1 ? "" : "s"
              } imported. ${failedCount} failed.`
            : `${successCount} panel user${
                successCount === 1 ? "" : "s"
              } imported successfully.`,
      });

      onSuccess?.();
      onClose();
    } catch (error) {
      notification.error({
        message: "Error",
        description:
          error.data?.message || "Failed to import selected panel users",
      });
    }
  };

  const isLoading = isRespondentsLoading || isRespondentsFetching;

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      footer={null}
      width={960}
      title="Import Panel Users"
      closeIcon={
        <div className="bg-secondary h-8 w-8 flex justify-center items-center rounded-full">
          <LuX className="text-red-400" size={20} />
        </div>
      }
      className="interpreter-modal"
      destroyOnClose
      centered
    >
      <Input
        value={searchInput}
        placeholder="Search respondents by name or email..."
        prefix={<SearchOutlined className="text-lg" />}
        suffix={
          searchInput ? (
            <CloseOutlined
              className="cursor-pointer hover:text-gray-600"
              onClick={() => setSearchInput("")}
            />
          ) : null
        }
        onChange={(e) => setSearchInput(e.target.value)}
        className="mb-4 h-11 !rounded-2xl bg-white"
      />

      <div className="overflow-hidden rounded-xl bg-white">
        <Table
          rowKey="_id"
          loading={isLoading}
          columns={columns}
          dataSource={respondentRows}
          pagination={false}
          rowSelection={rowSelection}
          className="forms-table panel-user-table"
          rowClassName="hover:bg-gray-50"
          scroll={{ y: 420 }}
          locale={{
            emptyText: isLoading
              ? "Loading respondents..."
              : "No respondents found",
          }}
        />
      </div>
      <div className="mt-6 flex justify-end">
        <div className="flex w-1/2 gap-3">
          <button
            type="button"
            onClick={onClose}
            className="w-[50%] h-14 btnSecondary border rounded-2xl text-gray-700 font-medium"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleImport}
            disabled={isImporting || importableSelectedCount === 0}
            className="w-[50%] h-14 bg-[#18181B] text-white rounded-2xl border-0 font-medium disabled:opacity-60"
          >
            {isImporting ? (
              <Spin size="small" />
            ) : (
              `Import${importableSelectedCount ? ` (${importableSelectedCount})` : ""}`
            )}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default ImportPanelUsersModal;
